import {
    Avatar,
    Colors,
    Pills,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { Node, TreeNodeProps } from '../../types';
import type { User } from '@/types/directory/User';

import { useAmelioTranslations } from '@/hooks';
import { getFullName, getInitials, getProfilePictureUrl } from '@/utils/user';

const REVIEWEE_RING_SHADOW =
    'shadow-[0_0_0_1.5px_var(--color-primary),0_0_0_10px_var(--color-primary-900)]';
const PROFILE_PIC_ALT = 'profile-pic';
const TOOLTIP_DELAY_MS = 200;

type RevieweeNodeProps = {
    node: Node<User>;
    revieweeBadgeLabel: string;
};

type ReviewerNodeProps = {
    node: Node<User>;
    onRemove: (id: string) => void;
};

const RevieweeNode = ({ node, revieweeBadgeLabel }: RevieweeNodeProps) => (
    <TooltipProvider>
        <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
            <TooltipTrigger asChild>
                <div>
                    <Avatar
                        alt={PROFILE_PIC_ALT}
                        initials={getInitials(node.data)}
                        src={getProfilePictureUrl(node.data?.profilePictureUrl || '')}
                        className={`${REVIEWEE_RING_SHADOW} [&_p]:text-4xl [&_p]:font-bold`}
                        size={'xl'}
                        label={
                            <Pills
                                size={'sm'}
                                variant={'primary'}
                                className={'text-center'}
                            >
                                {revieweeBadgeLabel}
                            </Pills>
                        }
                    />
                </div>
            </TooltipTrigger>

            <TooltipContent side={'top'}>
                <p>{getFullName(node.data)}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

const ReviewerNode = ({ node, onRemove }: ReviewerNodeProps) => (
    <TooltipProvider>
        <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
            <TooltipTrigger asChild>
                <div>
                    <Avatar
                        alt={PROFILE_PIC_ALT}
                        cornerBadge
                        src={getProfilePictureUrl(node.data?.profilePictureUrl || '')}
                        borderColor={Colors.SECONDARY}
                        borderSize={'ring-1'}
                        size={'sm'}
                        label={
                            <Pills
                                size={'sm'}
                                variant={'purple'}
                                className={'max-w-40 text-center'}
                            >
                                <span className={'block truncate'}>{getFullName(node.data)}</span>
                            </Pills>
                        }
                        onCornerBadgeClick={() => onRemove(node.data?.id || '')}
                    />
                </div>
            </TooltipTrigger>

            <TooltipContent side={'top'}>
                <p>{getFullName(node.data)}</p>
            </TooltipContent>
        </Tooltip>
    </TooltipProvider>
);

export const TreeNode = ({ node, onRemove, dataTestId }: TreeNodeProps) => {
    const { t } = useAmelioTranslations();
    const isReviewee = node.isCenter;

    return (
        <div
            data-testid={dataTestId}
            className={
                'absolute -translate-x-1/2 -translate-y-1/2 transform transition-all duration-500 ease-out'
            }
            style={{
                left: `${node.x}%`,
                top: `${node.y}%`,
                zIndex: isReviewee ? 10 : 5,
            }}
        >
            <div className={'flex flex-col items-center gap-1 sm:gap-1.5'}>
                {isReviewee ? (
                    <RevieweeNode
                        node={node}
                        revieweeBadgeLabel={t(
                            'evaluation360AssignReviewers.assignReviewers.revieweeBadge',
                        )}
                    />
                ) : (
                    <ReviewerNode
                        node={node}
                        onRemove={onRemove}
                    />
                )}
            </div>
        </div>
    );
};
